/**
 * The schedules register, the plan detail with its activities, and the activity detail
 * with the date negotiation.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * WORK_ORDER_M3_CONFIGURATION_AND_SURFACE.md section 4.2 screen 3. The plan view carries
 * the endorse and approve flow, the calendar advisory that persists in front of every
 * approver, and the BLOCK refusal's human text. The activity view carries the team as
 * the server publishes it (empty for a counterparty, on purpose) and the negotiation
 * thread where a CONFIRM is the only move that changes the plan of record.
 *
 * A counterparty sees its own lines and not the plan's: that is one SQL term on the
 * server, and the screen renders the rows it was given.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { monthCells } from "../calendar/dateMath";
import { MonthGrid } from "../calendar/MonthGrid";
import { AttentionLegend, activityAttention, enforcementShade } from "../calendar/model";
import {
  createActivity,
  getActivity,
  getSchedule,
  listActivities,
  listAssessors,
  listCalendarPeriods,
  listNegotiation,
  listPlanningProjects,
  listScheduleTransitions,
  listSchedules,
  transitionActivity,
  transitionSchedule,
  type Activity,
  type Assessor,
  type CalendarConflict,
  type NegotiationEntry,
  type NegotiationMove,
  type ScheduleSummary,
  type TransitionEntry,
} from "../api/schedules";
import { listCandidates } from "../api/itps";
import { getTaxonomies, listContracts } from "../api/portfolio";
import { ApiError } from "../api/client";
import { useToken } from "../auth/sessionContext";
import { strings, term } from "../brand/strings";
import { HistoryButton } from "../components/HistoryDrawer";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import {
  RefusalBox,
  AbsencePanel,
  Breadcrumbs,
  EmptyState,
  Facts,
  LoadingLine,
  SkeletonFacts,
  SkeletonRows,
  RegisterTh,
  Section,
  StateChip,
  day,
  filterSet,
  stamp,
  type HeaderSort,
} from "./parts";
import { SignatureBlock, YearGrid, candidateYears } from "./YearGrid";
import { useQuery } from "./useQuery";
import { ServerField } from "../lib/serverErrors";
import { CreateScheduleDialog } from "./CreateScheduleDialog";
import { DateCheck, DateMoveDialog, NegotiationLine, moveLabel } from "./planning";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { conductActivity } from "../api/conduct";
import { ConductDialog } from "./Conduct";
import { QueueBanner } from "../components/QueueBanner";
import { QUEUE_PARAM, readScheduleSignQueue } from "../nav/queues";

/**
 * The schedule register's own lifecycle tokens; the State header filter lists these
 * and not the whole workflow vocabulary (the same rule the other registers follow).
 */
/** A register card: its title, then its states or its DataTable (UX_SPEC 2.1). */
function RegisterCard({
  title,
  children,
}: {
  readonly title?: string | undefined;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="q-ui mb-(--q-s2) flex min-w-0 flex-col gap-3">
      {title !== undefined && (
        <h3 className="text-xs tracking-[0.16em] text-muted-foreground uppercase">{title}</h3>
      )}
      {children}
    </section>
  );
}

/** The schedule register columns: what each cell says in plain words. */
function scheduleRegisterColumns(): RegisterColumn<ScheduleSummary>[] {
  const copy = strings.schedules;
  return [
    {
      id: "plan",
      label: copy.columnPlan,
      text: (row) => `${row.humanId} ${row.title}`,
      sortValue: (row) => row.humanId,
      className: "min-w-[14rem]",
      render: (row) => (
        <>
          <Link
            to={`/schedules/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[34rem] [overflow-wrap:anywhere] text-word">{row.title}</div>
        </>
      ),
    },
    {
      id: "project",
      label: copy.columnProject,
      text: (row) => row.projectName,
      sortValue: (row) => row.projectName,
      filter: true,
    },
    {
      id: "stage",
      label: copy.columnStage,
      text: (row) => row.stageLabel ?? "",
      sortValue: (row) => row.stageLabel ?? "",
      filter: true,
    },
    {
      id: "year",
      label: copy.columnYear,
      text: (row) => String(row.year),
      sortValue: (row) => row.year,
      className: "text-right tabular-nums",
      filter: true,
    },
    {
      id: "revision",
      label: copy.columnRevision,
      text: (row) => String(row.revision),
      sortValue: (row) => row.revision,
      className: "text-right tabular-nums",
    },
    {
      id: "state",
      label: copy.columnState,
      text: (row) => term(row.workflowState),
      filter: true,
      render: (row) => <StateChip state={row.workflowState} />,
    },
  ];
}

export function SchedulesPage(): React.JSX.Element {
  const token = useToken();
  const navigate = useNavigate();
  const [parameters] = useSearchParams();
  const stateFilter = parameters.get("state");
  const stateSet = filterSet(stateFilter);
  const [creatingSchedule, setCreatingSchedule] = useState(false);

  // Multi-select column filter, comma-joined in the URL (M5 P6 seat V #6/#8).
  // Wave W1 (R13): `?queue=sign` opens the register as the signatures queue, through the
  // SAME read Home counts.
  const signQueue = parameters.get("queue") === QUEUE_PARAM.scheduleSign;
  const register = useQuery(
    async (signal) =>
      signQueue
        ? { items: await readScheduleSignQueue(token, signal) }
        : listSchedules(token, { signal }),
    [token, signQueue],
  );
  const planningProjects = useQuery((signal) => listPlanningProjects(token, signal), [token]);
  const mayCreate = planningProjects.kind === "ready" && planningProjects.data.length > 0;

  const columns = useMemo(() => scheduleRegisterColumns(), []);

  const items = useMemo(() => {
    if (register.kind !== "ready") return [];
    return register.data.items.filter(
      (schedule) => stateSet.length === 0 || stateSet.includes(schedule.workflowState),
    );
  }, [register, stateSet]);

  return (
    <>
      <header className="q-ui mb-(--q-s1) flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[28rem]">
          <h2 className="q-shell__heading">{strings.schedules.heading}</h2>
          <p className="q-shell__lede">{strings.schedules.lede}</p>
        </div>
        {mayCreate && (
          <div className="q-actions-row m-0" data-testid="schedules-actions">
            <button
              type="button"
              className="q-button q-mini--primary"
              data-testid="schedule-create-open"
              onClick={() => {
                setCreatingSchedule(true);
              }}
            >
              {strings.schedules.createSchedule}
            </button>
          </div>
        )}
      </header>

      {signQueue && (
        <QueueBanner
          label={strings.queues.scheduleSign.label}
          count={register.kind === "ready" ? register.data.items.length : null}
          wholeRegister="/schedules"
        />
      )}

      <RegisterCard>
        {register.kind === "loading" && <SkeletonRows />}
        {register.kind === "absence" && <AbsencePanel />}
        {register.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {register.kind === "ready" &&
          (items.length === 0 ? (
            <EmptyState title={strings.schedules.emptyTitle} body={strings.schedules.emptyBody} />
          ) : (
            <DataTable
              data={items}
              columns={columns}
              caption={strings.schedules.heading}
              testId="schedules"
              getRowId={(row) => row.id}
            />
          ))}
      </RegisterCard>

      {creatingSchedule && planningProjects.kind === "ready" && (
        <CreateScheduleDialog
          projects={planningProjects.data}
          onClose={() => {
            setCreatingSchedule(false);
          }}
          onCreated={(created) => {
            setCreatingSchedule(false);
            void navigate(`/schedules/${created.id}`);
          }}
        />
      )}
    </>
  );
}

/** The plan's lifecycle events, the server's tokens, and their plain labels. */
const EVENT_LABELS: Record<string, string> = {
  SUBMIT: strings.schedules.transitionSubmit,
  ENDORSE: strings.schedules.transitionEndorse,
  APPROVE: strings.schedules.transitionApprove,
  REJECT: strings.schedules.transitionReject,
  DISTRIBUTE: strings.schedules.transitionDistribute,
};

export function ScheduleDetailPage(): React.JSX.Element {
  const token = useToken();
  const { scheduleId = "" } = useParams();
  const [reload, setReload] = useState(0);
  // The action-dialog pattern (M5 P5e #8), wave W2: the dialog keeps its own refusal and
  // stays open until the server says yes; the toast reports the outcome on the page.
  const [dialogEvent, setDialogEvent] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<ApiError | null>(null);
  const [dialogComment, setDialogComment] = useState("");
  const [dialogBusy, setDialogBusy] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);
  const [advisories, setAdvisories] = useState<CalendarConflict[] | null>(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [moveDialog, setMoveDialog] = useState<{
    activity: Activity;
    move: NegotiationMove;
  } | null>(null);
  // The year calendar is the DEFAULT (M5 P5e #8): calendar first, then month, then the
  // list, then history - that order top to bottom.
  const [view, setView] = useState<"table" | "grid" | "month">("grid");
  const [gridYear, setGridYear] = useState<number | null>(null);
  const [showShutdown, setShowShutdown] = useState(true);
  const [showRamadan, setShowRamadan] = useState(true);
  const [monthCursor, setMonthCursor] = useState<{ year: number; month: number } | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [tableSort, setTableSort] = useState<HeaderSort>({ key: null, dir: "asc" });
  const [tableStatusFilter, setTableStatusFilter] = useState<readonly string[]>([]);

  const detail = useQuery(() => getSchedule(token, scheduleId), [token, scheduleId, reload]);
  const activities = useQuery(() => listActivities(token, scheduleId), [token, scheduleId, reload]);
  const history = useQuery(
    () => listScheduleTransitions(token, scheduleId),
    [token, scheduleId, reload],
  );
  const periods = useQuery(() => listCalendarPeriods(token), [token]);

  const reread = (): void => {
    setReload((n) => n + 1);
  };

  function openEvent(event: string): void {
    setDialogEvent(event);
    setDialogError(null);
    setDialogComment("");
  }

  async function move(event: string): Promise<void> {
    setDialogBusy(true);
    setDialogError(null);
    try {
      const outcome = await transitionSchedule(token, scheduleId, event.toLowerCase(), {
        comment: dialogComment.trim().length > 0 ? dialogComment.trim() : null,
      });
      setAdvisories(outcome.warnings.length > 0 ? outcome.warnings : null);
      setToast({
        message: strings.schedules.actionToastDone(EVENT_LABELS[event] ?? event),
        tone: "info",
      });
      setDialogEvent(null);
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setDialogError(error);
      }
    } finally {
      setDialogBusy(false);
    }
  }

  if (detail.kind === "absence") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.schedules.detailHeading}</h2>
        <AbsencePanel />
      </>
    );
  }
  if (detail.kind !== "ready") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.schedules.detailHeading}</h2>
        {detail.kind === "loading" && <SkeletonFacts fields={6} />}
        {detail.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
      </>
    );
  }

  const schedule = detail.data;
  const rows = activities.kind === "ready" ? activities.data.items : [];
  // Wave W2: the dates waiting on THIS caller, each with the moves the server offers.
  const toAnswer = rows.filter((one) => one.availableMoves.length > 0);
  const needsReason = dialogEvent === "REJECT";

  return (
    <>
      <Breadcrumbs
        trail={[
          { label: strings.schedules.heading, to: "/schedules" },
          { label: schedule.humanId },
        ]}
      />
      <h2 className="q-shell__heading" data-testid="schedule-title">
        {schedule.humanId} {schedule.title}
      </h2>
      <p className="q-shell__lede">
        <StateChip state={schedule.workflowState} />
      </p>

      {advisories !== null && advisories.length > 0 && (
        <div className="q-banner" role="status" data-testid="advisory-list">
          <p className="q-banner__heading">{strings.schedules.advisoryHeading}</p>
          {advisories.map((advisory, index) => (
            <p className="q-thread__body" key={index}>
              {strings.schedules.advisoryBody(advisory.periodName)}{" "}
              {/* Wave W1.5 alert sweep: a callout that names a record links to it. */}
              <Link
                className="q-link"
                to={`/calendar?day=${advisory.startsOn}`}
                data-testid="advisory-period-link"
              >
                {strings.schedules.advisoryOpenPeriod(advisory.periodName)}
              </Link>
            </p>
          ))}
        </div>
      )}
      {toast !== null && (
        <ToastBar
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}

      {/* The lifecycle acts live AT THE TOP (M5 P5e #8). Wave W2, the CapabilityUpFront
          law: an act is drawn only when the server lists it for THIS caller now (state
          machine and predicate together), so it always works when it appears. Otherwise
          the row says, in words, what the plan waits for. */}
      <div className="q-actions-row q-gap-top" data-testid="schedule-lifecycle">
        <HistoryButton
          objectType="SCHEDULE"
          objectId={schedule.id}
          recordLabel={schedule.humanId}
        />
        {schedule.availableEvents.map((event) => (
          <button
            key={event}
            type="button"
            className={
              event === "REJECT"
                ? "q-button q-button--quiet"
                : "q-button q-button--quiet q-mini--primary"
            }
            data-testid={`lifecycle-${event.toLowerCase()}`}
            onClick={() => {
              openEvent(event);
            }}
          >
            {EVENT_LABELS[event] ?? event}
          </button>
        ))}
        {schedule.mayPlanActivity && (
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="plan-activity-open"
            onClick={() => {
              setPlanDialogOpen(true);
            }}
          >
            {strings.schedules.planActivity}
          </button>
        )}
        {schedule.availableEvents.length === 0 && (
          <p className="q-note" data-testid="schedule-waiting">
            {strings.schedules.waiting[schedule.workflowState] ?? ""}
          </p>
        )}
      </div>

      <div className="q-section-bands" data-testid="schedule-detail-sections">
        <Section heading={strings.schedules.detailHeading}>
          <Facts
            entries={[
              [strings.common.humanId, <span key="id">{schedule.humanId}</span>],
              [strings.schedules.columnProject, <span key="project">{schedule.projectName}</span>],
              [strings.schedules.columnStage, <span key="stage">{schedule.stageLabel ?? ""}</span>],
              [strings.schedules.columnYear, <span key="year">{String(schedule.year)}</span>],
            ]}
          />
        </Section>

        {dialogEvent !== null && (
          <ActionDialog
            open
            title={EVENT_LABELS[dialogEvent] ?? dialogEvent}
            error={dialogError}
            onClose={() => {
              setDialogEvent(null);
            }}
          >
            <p className="q-shell__lede">
              {strings.schedules.actionConfirm(EVENT_LABELS[dialogEvent] ?? dialogEvent)}
            </p>
            {needsReason && (
              <div className="q-field">
                <label className="q-field__label" htmlFor="schedule-reject-reason">
                  {strings.schedules.rejectReasonLabel}
                </label>
                <ServerField field="comment">
                  <textarea
                    id="schedule-reject-reason"
                    className="q-textarea"
                    value={dialogComment}
                    onChange={(event) => {
                      setDialogComment(event.target.value);
                    }}
                  />
                </ServerField>
              </div>
            )}
            <div className="q-actions-row">
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="dialog-confirm"
                disabled={dialogBusy || (needsReason && dialogComment.trim().length === 0)}
                onClick={() => {
                  void move(dialogEvent);
                }}
              >
                {EVENT_LABELS[dialogEvent] ?? dialogEvent}
              </button>
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="dialog-cancel"
                onClick={() => {
                  setDialogEvent(null);
                }}
              >
                {strings.common.cancel}
              </button>
            </div>
          </ActionDialog>
        )}

        {planDialogOpen && (
          <PlanActivityDialog
            schedule={schedule}
            onClose={() => {
              setPlanDialogOpen(false);
            }}
            onPlanned={(humanId, warnings) => {
              setPlanDialogOpen(false);
              setAdvisories(warnings.length > 0 ? warnings : null);
              setToast({ message: strings.schedules.planned(humanId), tone: "info" });
              reread();
            }}
          />
        )}

        {moveDialog !== null && (
          <DateMoveDialog
            activity={moveDialog.activity}
            move={moveDialog.move}
            onClose={() => {
              setMoveDialog(null);
            }}
            onDone={(message) => {
              setMoveDialog(null);
              setToast({ message, tone: "info" });
              reread();
            }}
          />
        )}

        {/* Wave W2: the contractor's acknowledge surface (and the operator's answer to a
          proposal). Every date waiting on THIS caller, with the moves the server
          offers for it, right here: no page to hunt for. */}
        {toAnswer.length > 0 && (
          <Section
            heading={strings.schedules.yourActivitiesHeading}
            lede={strings.schedules.yourActivitiesLede}
          >
            <ul className="q-answer-list" data-testid="dates-to-answer">
              {toAnswer.map((activity) => (
                <li key={activity.id} className="q-answer-row" data-testid="date-to-answer">
                  <div className="q-answer-row__what">
                    <Link to={`/activities/${activity.id}`} className="q-register__mono">
                      {activity.humanId}
                    </Link>{" "}
                    {activity.verificationTypeLabel}
                    {activity.purpose !== null && (
                      <span className="q-note"> · {activity.purpose}</span>
                    )}
                  </div>
                  <div className="q-answer-row__when">
                    <strong>{day(activity.plannedDate)}</strong>{" "}
                    <NegotiationLine activity={activity} />
                  </div>
                  <div className="q-actions-row q-answer-row__acts">
                    {activity.availableMoves.map((one) => (
                      <button
                        key={one}
                        type="button"
                        className={
                          one === "ACKNOWLEDGE" || one === "CONFIRM"
                            ? "q-button q-mini--primary"
                            : "q-button q-button--quiet"
                        }
                        data-testid={`answer-${one.toLowerCase()}`}
                        onClick={() => {
                          setMoveDialog({ activity, move: one });
                        }}
                      >
                        {moveLabel(one, activity)}
                      </button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section heading={strings.schedules.activitiesHeading}>
          <div className="q-actions-row" data-testid="schedule-view-toggle">
            <button
              type="button"
              className="q-button q-button--quiet"
              aria-pressed={view === "grid"}
              data-testid="schedule-view-year"
              onClick={() => {
                setView("grid");
              }}
            >
              {strings.schedules.viewYearGrid}
            </button>
            <button
              type="button"
              className="q-button q-button--quiet"
              aria-pressed={view === "month"}
              data-testid="schedule-view-month"
              onClick={() => {
                setView("month");
              }}
            >
              {strings.schedules.viewMonth}
            </button>
            <button
              type="button"
              className="q-button q-button--quiet"
              aria-pressed={view === "table"}
              onClick={() => {
                setView("table");
              }}
            >
              {strings.schedules.viewTable}
            </button>
          </div>

          {/* Wave W2: every calendar view of the plan carries the legend. Ruling C: its feed
              carries no actionable_by_me, so "Waiting on you" is not offered here. */}
          <AttentionLegend showRfis={false} showAttention={false} />

          {view === "grid" && activities.kind === "ready" && (
            <>
              <div className="q-actions-row" data-testid="year-grid-controls">
                <label className="q-field__label" htmlFor="year-grid-year">
                  {strings.schedules.columnYear}
                </label>
                <select
                  id="year-grid-year"
                  className="q-select"
                  value={gridYear ?? schedule.year}
                  onChange={(event) => {
                    setGridYear(Number(event.target.value));
                  }}
                >
                  {candidateYears(schedule.year, activities.data.items).map((entry) => (
                    <option key={entry} value={entry}>
                      {entry}
                    </option>
                  ))}
                </select>
                <label>
                  <input
                    type="checkbox"
                    checked={showShutdown}
                    onChange={(event) => {
                      setShowShutdown(event.target.checked);
                    }}
                  />{" "}
                  {strings.schedules.showShutdown}
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={showRamadan}
                    onChange={(event) => {
                      setShowRamadan(event.target.checked);
                    }}
                  />{" "}
                  {strings.schedules.showRamadan}
                </label>
              </div>
              <div data-testid="schedule-yeargrid-scroll">
                <YearGrid
                  year={gridYear ?? schedule.year}
                  activities={activities.data.items}
                  periods={periods.kind === "ready" ? periods.data : []}
                  showShutdown={showShutdown}
                  showRamadan={showRamadan}
                />
              </div>
              {history.kind === "ready" && <SignatureBlock history={history.data} />}
            </>
          )}

          {view === "month" && activities.kind === "ready" && (
            <MonthCalendar
              year={monthCursor?.year ?? new Date().getFullYear()}
              month={monthCursor?.month ?? new Date().getMonth()}
              activities={activities.data.items}
              periods={periods.kind === "ready" ? periods.data : []}
              openDay={openDay}
              onCursor={(year, month) => {
                setMonthCursor({ year, month });
                setOpenDay(null);
              }}
              onSelectDay={setOpenDay}
            />
          )}

          {view === "table" &&
            activities.kind === "ready" &&
            activities.data.items.length === 0 && (
              <EmptyState title={strings.common.empty} body={strings.schedules.emptyBody} />
            )}
          {view === "table" && activities.kind === "ready" && activities.data.items.length > 0 && (
            <div className="q-register-viewport">
              <table className="q-register">
                <thead>
                  <tr>
                    <th scope="col">{strings.schedules.columnActivity}</th>
                    <RegisterTh
                      label={strings.schedules.columnDate}
                      sortKey="date"
                      sort={tableSort}
                      onSort={(key) => {
                        setTableSort((current) =>
                          current.key === key && current.dir === "asc"
                            ? { key, dir: "desc" }
                            : { key, dir: "asc" },
                        );
                      }}
                    />
                    <th scope="col">{strings.schedules.columnDateState}</th>
                    <th scope="col">{strings.schedules.columnType}</th>
                    <th scope="col">{strings.schedules.columnContractor}</th>
                    <RegisterTh
                      label={strings.schedules.columnStatus}
                      sortKey="status"
                      sort={tableSort}
                      onSort={(key) => {
                        setTableSort((current) =>
                          current.key === key && current.dir === "asc"
                            ? { key, dir: "desc" }
                            : { key, dir: "asc" },
                        );
                      }}
                      filter={{
                        values: tableStatusFilter,
                        options: [
                          ...new Map(
                            activities.data.items.map((one: Activity) => [
                              one.status,
                              term(one.status),
                            ]),
                          ).entries(),
                        ].map(([value, label]) => ({ value, label })),
                        onChange: setTableStatusFilter,
                      }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {(tableStatusFilter.length
                    ? activities.data.items.filter((one: Activity) =>
                        tableStatusFilter.includes(one.status),
                      )
                    : [...activities.data.items]
                  )
                    .slice()
                    .sort((a: Activity, b: Activity) => {
                      const dir = tableSort.dir === "asc" ? 1 : -1;
                      if (tableSort.key === "date") {
                        return dir * a.plannedDate.localeCompare(b.plannedDate);
                      }
                      if (tableSort.key === "status") {
                        return dir * a.status.localeCompare(b.status);
                      }
                      return 0;
                    })
                    .map((activity: Activity) => (
                      <tr key={activity.id}>
                        <td>
                          <Link to={`/activities/${activity.id}`} className="q-register__mono">
                            {activity.humanId}
                          </Link>
                          {activity.purpose !== null && (
                            <div className="q-register__wrap">{activity.purpose}</div>
                          )}
                        </td>
                        <td data-testid="activity-planned-date">{day(activity.plannedDate)}</td>
                        <td>
                          <NegotiationLine activity={activity} />
                        </td>
                        <td>{activity.verificationTypeLabel}</td>
                        <td>{activity.contractorPartyName}</td>
                        <td>
                          <StateChip state={activity.status} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section heading={strings.schedules.calendarHeading}>
          {periods.kind === "ready" && periods.data.length === 0 && (
            <p className="q-note">{strings.common.empty}</p>
          )}
          {periods.kind === "ready" && periods.data.length > 0 && (
            <div className="q-register-viewport">
              <table className="q-register">
                <thead>
                  <tr>
                    <th scope="col">{strings.schedules.columnDate}</th>
                    <th scope="col">{strings.common.status}</th>
                    <th scope="col">{strings.schedules.endorsementLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.data.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        {entry.name}: {day(entry.startsOn)} to {day(entry.endsOn)}
                      </td>
                      <td>{entry.kindLabel}</td>
                      <td>
                        <StateChip
                          state={null}
                          label={
                            entry.enforcement === "BLOCK"
                              ? strings.configuration.enforcementBlock
                              : strings.configuration.enforcementWarn
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section heading={strings.schedules.historyHeading}>
          {history.kind === "ready" && <PlanHistory entries={history.data} />}
        </Section>
      </div>
    </>
  );
}

/**
 * Plan an activity into a DRAFT schedule (wave W2). Mahmoud's walk: "Check the date" did
 * nothing, and "Plan the activity" failed naming "Activity type", a field that was not on
 * the form (the planner had to type an identifier), with the refusal behind the dialog.
 * Now every field the server can name is on the form, the type is a list, the date check
 * answers, and the dialog keeps its refusal inside and stays open until it succeeds.
 */
function PlanActivityDialog({
  schedule,
  onClose,
  onPlanned,
}: {
  readonly schedule: ScheduleSummary;
  readonly onClose: () => void;
  readonly onPlanned: (humanId: string, warnings: CalendarConflict[]) => void;
}): React.JSX.Element {
  const token = useToken();
  const candidates = useQuery(() => listCandidates(token, { unplannedOnly: true }), [token]);
  const taxonomies = useQuery(() => getTaxonomies(token), [token]);
  const contracts = useQuery(
    () => listContracts(token, { projectId: schedule.projectId }),
    [token, schedule.projectId],
  );
  const [candidateId, setCandidateId] = useState("");
  const [contractorId, setContractorId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const projectContracts = contracts.kind === "ready" ? contracts.data.items : [];
  const plannable = (candidates.kind === "ready" ? candidates.data.items : []).filter(
    (one) => one.mayPlan && projectContracts.some((contract) => contract.id === one.contractId),
  );
  const candidate = plannable.find((one) => one.id === candidateId);
  const contractors = [
    ...new Map(
      projectContracts.map((one) => [one.contractorPartyId, one.contractorPartyName]),
    ).entries(),
  ];
  const types = (taxonomies.kind === "ready" ? taxonomies.data.verificationTypes : []).filter(
    (one) => one.id !== undefined,
  );
  const party = candidate?.contractorPartyId ?? contractorId;
  const missing =
    party.length === 0
      ? strings.schedules.planNeedsCandidateOrContractor
      : typeId.length === 0
        ? strings.schedules.planNeedsType
        : plannedDate.length === 0
          ? strings.schedules.planNeedsDate
          : null;

  async function plan(): Promise<void> {
    if (missing !== null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await createActivity(token, schedule.id, {
        contractor_party_id: party,
        contract_id: candidate?.contractId ?? null,
        verification_type_id: typeId,
        planned_date: plannedDate,
        planned_end_date: endDate.length > 0 ? endDate : null,
        planning_candidate_id: candidate?.id ?? null,
        purpose: purpose.trim().length > 0 ? purpose.trim() : null,
      });
      onPlanned(created.humanId, created.warnings);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={strings.schedules.createActivityHeading}
      onClose={onClose}
      error={error}
    >
      <div className="q-field">
        <label className="q-field__label" htmlFor="activity-candidate">
          {strings.schedules.candidateLabel}
        </label>
        <ServerField field="planning_candidate_id">
          <select
            id="activity-candidate"
            className="q-select"
            value={candidateId}
            onChange={(event) => {
              setCandidateId(event.target.value);
            }}
          >
            <option value="">{strings.schedules.candidateNone}</option>
            {plannable.map((one) => (
              <option key={one.id} value={one.id}>
                {one.itpHumanId} {one.itemNo} {one.activityDescription} ({one.contractorPartyName})
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      {candidate === undefined ? (
        <div className="q-field">
          <label className="q-field__label" htmlFor="activity-contractor">
            {strings.schedules.contractorLabel}
          </label>
          <ServerField field="contractor_party_id">
            <select
              id="activity-contractor"
              className="q-select"
              value={contractorId}
              onChange={(event) => {
                setContractorId(event.target.value);
              }}
            >
              <option value="" />
              {contractors.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </ServerField>
        </div>
      ) : (
        <p className="q-note" data-testid="activity-contractor-from-candidate">
          {strings.schedules.contractorLabel}: {candidate.contractorPartyName} ·{" "}
          {candidate.contractHumanId}
        </p>
      )}
      <div className="q-field">
        <label className="q-field__label" htmlFor="activity-type">
          {strings.schedules.typeSelectLabel}
        </label>
        <ServerField field="verification_type_id">
          <select
            id="activity-type"
            className="q-select"
            value={typeId}
            onChange={(event) => {
              setTypeId(event.target.value);
            }}
          >
            <option value="" />
            {types.map((one) => (
              <option key={one.token} value={one.id}>
                {one.label}
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="activity-date">
          {strings.schedules.plannedDateLabel}
        </label>
        <ServerField field="planned_date">
          <input
            id="activity-date"
            className="q-input"
            type="date"
            data-testid="planned-date-input"
            value={plannedDate}
            onChange={(event) => {
              setPlannedDate(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="activity-end-date">
          {strings.schedules.endDateLabel}
        </label>
        <ServerField field="planned_end_date">
          <input
            id="activity-end-date"
            className="q-input"
            type="date"
            value={endDate}
            onChange={(event) => {
              setEndDate(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <DateCheck
        projectId={schedule.projectId}
        plannedDate={plannedDate}
        plannedEndDate={endDate.length > 0 ? endDate : null}
      />
      <div className="q-field">
        <label className="q-field__label" htmlFor="activity-purpose">
          {strings.schedules.purposeLabel}
        </label>
        <ServerField field="purpose">
          <input
            id="activity-purpose"
            className="q-input"
            value={purpose}
            onChange={(event) => {
              setPurpose(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {missing !== null && <p className="q-note">{missing}</p>}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="plan-activity-confirm"
          disabled={busy || missing !== null}
          onClick={() => {
            void plan();
          }}
        >
          {strings.schedules.planActivity}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

function PlanHistory({ entries }: { entries: TransitionEntry[] }): React.JSX.Element {
  if (entries.length === 0) {
    return <p className="q-note">{strings.common.empty}</p>;
  }
  return (
    <div className="q-thread">
      {entries.map((entry) => (
        <article className="q-thread__entry" key={entry.id}>
          <p className="q-thread__meta">
            <strong>{term(entry.event)}</strong> · {term(entry.fromState)} → {term(entry.toState)} ·{" "}
            {day(entry.at)}
            {entry.actorDisplayName !== null && ` · ${entry.actorDisplayName}`}
          </p>
          {entry.comment !== null && <p className="q-thread__body">{entry.comment}</p>}
        </article>
      ))}
    </div>
  );
}

const ACTIVITY_EVENT_LABELS: Record<string, string> = {
  CONDUCT: strings.schedules.activityConduct,
  CLOSE: strings.schedules.activityClose,
  CANCEL: strings.schedules.activityCancel,
};

export function ActivityDetailPage(): React.JSX.Element {
  const token = useToken();
  const { activityId = "" } = useParams();
  const [reload, setReload] = useState(0);
  // Wave W2: every act this page draws came from the server's lists for THIS caller
  // (`availableMoves` for the date, `availableEvents` for the status), and every dialog
  // keeps its own refusal inside itself.
  const [moveDialog, setMoveDialog] = useState<NegotiationMove | null>(null);
  const [eventDialog, setEventDialog] = useState<string | null>(null);
  const [eventReason, setEventReason] = useState("");
  const [eventError, setEventError] = useState<ApiError | null>(null);
  const [eventBusy, setEventBusy] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);
  // The inspection day: the activity is conducted with its program's checklist. The
  // record opens on the first conduct and is continued after that.
  const [conductId, setConductId] = useState<string | null>(null);
  const [conductError, setConductError] = useState<ApiError | null>(null);
  const [conductBusy, setConductBusy] = useState(false);

  const activity = useQuery(() => getActivity(token, activityId), [token, activityId, reload]);
  const team = useQuery(() => listAssessors(token, activityId), [token, activityId, reload]);
  const negotiation = useQuery(
    () => listNegotiation(token, activityId),
    [token, activityId, reload],
  );

  const reread = (): void => {
    setReload((n) => n + 1);
  };

  async function activityTransition(event: string): Promise<void> {
    setEventBusy(true);
    setEventError(null);
    try {
      await transitionActivity(token, activityId, event.toLowerCase(), {
        comment: eventReason.trim().length > 0 ? eventReason.trim() : null,
      });
      setToast({
        message: strings.schedules.actionToastDone(ACTIVITY_EVENT_LABELS[event] ?? event),
        tone: "info",
      });
      setEventDialog(null);
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setEventError(error);
      }
    } finally {
      setEventBusy(false);
    }
  }

  async function openConduct(existing: string | null): Promise<void> {
    if (existing !== null) {
      setConductId(existing);
      return;
    }
    setConductBusy(true);
    setConductError(null);
    try {
      setConductId(await conductActivity(token, activityId));
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setConductError(error);
      }
    } finally {
      setConductBusy(false);
    }
  }

  if (activity.kind === "absence") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.schedules.columnActivity}</h2>
        <AbsencePanel />
      </>
    );
  }
  if (activity.kind !== "ready") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.schedules.columnActivity}</h2>
        {activity.kind === "loading" && <LoadingLine label={strings.common.loading} />}
        {activity.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
      </>
    );
  }

  const record: Activity = activity.data;
  const needsReason = eventDialog === "CANCEL";

  return (
    <>
      <Breadcrumbs
        trail={[{ label: strings.schedules.heading, to: "/schedules" }, { label: record.humanId }]}
      />
      <h2 className="q-shell__heading">
        {record.humanId} {record.verificationTypeLabel}
      </h2>
      <p className="q-shell__lede">
        <StateChip state={record.status} />{" "}
        <span data-testid="activity-planned-date">{day(record.plannedDate)}</span>
        {record.plannedEndDate !== null && <span> to {day(record.plannedEndDate)}</span>}{" "}
        <NegotiationLine activity={record} />
      </p>

      {toast !== null && (
        <ToastBar
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}

      {/* The date's answer AT THE TOP: exactly the moves the server offers this caller. */}
      <div className="q-actions-row q-gap-top" data-testid="negotiation-actions">
        <HistoryButton
          objectType="SCHEDULED_ACTIVITY"
          objectId={record.id}
          recordLabel={record.humanId}
        />
        {record.availableMoves.map((one) => (
          <button
            key={one}
            type="button"
            className={
              one === "ACKNOWLEDGE" || one === "CONFIRM"
                ? "q-button q-mini--primary"
                : "q-button q-button--quiet"
            }
            data-testid={`negotiation-${one.toLowerCase()}`}
            onClick={() => {
              setMoveDialog(one);
            }}
          >
            {moveLabel(one, record)}
          </button>
        ))}
        {record.availableEvents.map((event) => (
          <button
            key={event}
            type="button"
            className="q-button q-button--quiet"
            data-testid={`activity-${event.toLowerCase()}`}
            onClick={() => {
              setEventDialog(event);
              setEventReason("");
              setEventError(null);
            }}
          >
            {ACTIVITY_EVENT_LABELS[event] ?? event}
          </button>
        ))}
        {record.mayConductInspection && (
          <button
            type="button"
            className="q-button q-mini--primary"
            data-testid="activity-conduct-inspection"
            disabled={conductBusy}
            aria-busy={conductBusy}
            onClick={() => {
              void openConduct(record.inspectionId);
            }}
          >
            {record.inspectionId === null ? strings.conduct.begin : strings.conduct.continueDraft}
          </button>
        )}
        {record.inspectionId !== null && (
          <Link
            className="q-button q-button--quiet"
            to={`/inspections/${record.inspectionId}`}
            data-testid="activity-inspection-record"
          >
            {strings.schedules.openInspectionRecord}
          </Link>
        )}
        {record.availableMoves.length === 0 &&
          record.availableEvents.length === 0 &&
          !record.mayConductInspection &&
          record.inspectionId === null && (
            <p className="q-note" data-testid="activity-nothing-yours">
              {strings.schedules.noMovesYours}
            </p>
          )}
      </div>
      {conductError !== null && <RefusalBox error={conductError} />}
      {conductId !== null && (
        <ConductDialog
          verificationId={conductId}
          onClose={() => {
            setConductId(null);
            reread();
          }}
          onChanged={(message) => {
            if (message !== "") {
              setConductId(null);
              setToast({ message, tone: "info" });
            }
            reread();
          }}
        />
      )}

      <div className="q-section-bands" data-testid="activity-detail-sections">
        <Section heading={strings.schedules.detailHeading}>
          <Facts
            entries={[
              [strings.common.humanId, <span key="id">{record.humanId}</span>],
              [
                strings.schedules.columnContractor,
                <span key="party">{record.contractorPartyName}</span>,
              ],
              [
                strings.schedules.columnType,
                <span key="type">{record.verificationTypeLabel}</span>,
              ],
              [strings.schedules.purposeLabel, <span key="purpose">{record.purpose ?? ""}</span>],
              [
                strings.schedules.checklistLabel,
                <span key="checklist">{record.checklistReference ?? ""}</span>,
              ],
            ]}
          />
        </Section>

        <Section heading={strings.schedules.assessorsHeading}>
          <div data-testid="assessor-list">
            {team.kind === "ready" && team.data.length === 0 && (
              <p className="q-note">{strings.schedules.assessorsEmpty}</p>
            )}
            {team.kind === "ready" &&
              team.data.map((entry: Assessor) => (
                <p className="q-note" key={entry.personId}>
                  {entry.personDisplayName}
                  {entry.isLead ? " · " : ""}
                  {entry.disciplineLabel ?? ""} {entry.roleLabel}
                </p>
              ))}
          </div>
        </Section>

        <Section heading={strings.schedules.negotiateHeading}>
          <div className="q-thread" data-testid="negotiation-thread">
            {negotiation.kind === "ready" && negotiation.data.length === 0 && (
              <p className="q-note">{strings.schedules.negotiationEmpty}</p>
            )}
            {negotiation.kind === "ready" &&
              negotiation.data.map((entry: NegotiationEntry) => (
                <article className="q-thread__entry" key={entry.id}>
                  <p className="q-thread__meta">
                    <strong>{entry.actorDisplayName}</strong> · {entry.actingPartyName} ·{" "}
                    {term(entry.move)}
                    {entry.proposedDate !== null && ` · ${day(entry.proposedDate)}`} ·{" "}
                    {stamp(entry.at)}
                  </p>
                  {entry.justification !== null && (
                    <p className="q-thread__body">{entry.justification}</p>
                  )}
                </article>
              ))}
          </div>
        </Section>
      </div>

      {moveDialog !== null && (
        <DateMoveDialog
          activity={record}
          move={moveDialog}
          onClose={() => {
            setMoveDialog(null);
          }}
          onDone={(message) => {
            setMoveDialog(null);
            setToast({ message, tone: "info" });
            reread();
          }}
        />
      )}

      {eventDialog !== null && (
        <ActionDialog
          open
          title={ACTIVITY_EVENT_LABELS[eventDialog] ?? eventDialog}
          error={eventError}
          onClose={() => {
            setEventDialog(null);
          }}
        >
          <p className="q-shell__lede">
            {strings.schedules.actionConfirm(ACTIVITY_EVENT_LABELS[eventDialog] ?? eventDialog)}
          </p>
          {needsReason && (
            <div className="q-field">
              <label className="q-field__label" htmlFor="activity-event-reason">
                {strings.schedules.rejectReasonLabel}
              </label>
              <ServerField field="comment">
                <textarea
                  id="activity-event-reason"
                  className="q-textarea"
                  value={eventReason}
                  onChange={(event) => {
                    setEventReason(event.target.value);
                  }}
                />
              </ServerField>
            </div>
          )}
          <div className="q-actions-row">
            <button
              type="button"
              className="q-button q-mini--primary"
              data-testid="dialog-confirm"
              disabled={eventBusy || (needsReason && eventReason.trim().length === 0)}
              onClick={() => {
                void activityTransition(eventDialog);
              }}
            >
              {ACTIVITY_EVENT_LABELS[eventDialog] ?? eventDialog}
            </button>
            <button
              type="button"
              className="q-button q-button--quiet"
              onClick={() => {
                setEventDialog(null);
              }}
            >
              {strings.common.cancel}
            </button>
          </div>
        </ActionDialog>
      )}
    </>
  );
}

/**
 * The month-grid view of ONE schedule's planned activities (Mahmoud, 2026-10-02): the
 * same records the list shows, laid out on the calendar month they are planned in, with
 * the calendar periods' enforcement shading behind them. The list stays the primary
 * view; the grid is a second lens over the same payload, never a second fetch.
 */
function MonthCalendar({
  year,
  month,
  activities,
  periods,
  openDay,
  onCursor,
  onSelectDay,
}: {
  year: number;
  month: number;
  activities: Activity[];
  periods: { startsOn: string; endsOn: string; enforcement: string }[];
  openDay: string | null;
  onCursor: (year: number, month: number) => void;
  onSelectDay: (iso: string) => void;
}): React.JSX.Element {
  const byDate = useMemo(() => {
    const map = new Map<string, Activity[]>();
    for (const activity of activities) {
      const list = map.get(activity.plannedDate) ?? [];
      list.push(activity);
      map.set(activity.plannedDate, list);
    }
    return map;
  }, [activities]);

  const cells = useMemo(
    () =>
      monthCells(year, month).map((cell) => {
        const dayActivities = byDate.get(cell.iso) ?? [];
        let shade: "block" | "warn" | null = null;
        for (const period of periods) {
          if (cell.iso >= period.startsOn && cell.iso <= period.endsOn) {
            const one = enforcementShade(period.enforcement);
            if (one === "block") {
              shade = "block";
            } else if (one === "warn" && shade === null) {
              shade = "warn";
            }
          }
        }
        return {
          ...cell,
          dots: dayActivities.map((one) => ({
            tone: activityAttention(one),
            label: `${one.humanId} · ${strings.schedules.negotiation[one.negotiationState] ?? term(one.status)}`,
          })),
          shade,
        };
      }),
    [year, month, byDate, periods],
  );

  const cursorLabel = new Date(Date.UTC(year, month, 1)).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const openActivities = openDay === null ? [] : (byDate.get(openDay) ?? []);

  return (
    <div data-testid="schedule-month-calendar">
      <div className="q-register-bar" style={{ alignItems: "center", gap: "var(--q-s0)" }}>
        <button
          type="button"
          className="q-button q-button--quiet"
          data-testid="schedule-month-prev"
          aria-label={strings.calendar.previous}
          onClick={() => {
            const first = new Date(Date.UTC(year, month - 1, 1));
            onCursor(first.getUTCFullYear(), first.getUTCMonth());
          }}
        >
          ←
        </button>
        <strong>{cursorLabel}</strong>
        <button
          type="button"
          className="q-button q-button--quiet"
          data-testid="schedule-month-next"
          aria-label={strings.calendar.next}
          onClick={() => {
            const first = new Date(Date.UTC(year, month + 1, 1));
            onCursor(first.getUTCFullYear(), first.getUTCMonth());
          }}
        >
          →
        </button>
      </div>
      <MonthGrid cells={cells} onSelectDay={onSelectDay} />
      {openDay !== null && (
        <div data-testid="schedule-month-day">
          <p>
            <strong>{openDay}</strong>
          </p>
          {openActivities.length === 0 ? (
            <p>{strings.common.empty}</p>
          ) : (
            <ul>
              {openActivities.map((one) => (
                <li key={one.id}>
                  <Link to={`/activities/${one.id}`}>
                    {one.humanId} · {one.verificationTypeLabel}
                  </Link>{" "}
                  <StateChip state={one.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
